export type UiStateStorageArea = 'local' | 'session';

export interface UiStateStorageConfig {
  key: string;
  namespace?: string;
  version?: number;
  area?: UiStateStorageArea;
}

interface PersistedEnvelope<T> {
  version: number;
  value: T;
}

const DEFAULT_NAMESPACE = 'xbox.ui';
const DEFAULT_VERSION = 1;

const getStorage = (area: UiStateStorageArea): Storage | null => {
  if (typeof window === 'undefined') {
    return null;
  }

  return area === 'session' ? window.sessionStorage : window.localStorage;
};

const resolveStorageKey = (config: UiStateStorageConfig): string => {
  const namespace = config.namespace || DEFAULT_NAMESPACE;
  return `${namespace}.${config.key}`;
};

export const readUiState = <T>(
  config: UiStateStorageConfig,
  fallbackValue: T
): T => {
  const storage = getStorage(config.area || 'local');
  if (!storage) {
    return fallbackValue;
  }

  try {
    const raw = storage.getItem(resolveStorageKey(config));
    if (!raw) {
      return fallbackValue;
    }

    const parsed = JSON.parse(raw) as PersistedEnvelope<T>;
    const expectedVersion = config.version || DEFAULT_VERSION;
    if (!parsed || parsed.version !== expectedVersion) {
      return fallbackValue;
    }

    return parsed.value;
  } catch {
    return fallbackValue;
  }
};

export const writeUiState = <T>(
  config: UiStateStorageConfig,
  value: T
): void => {
  const storage = getStorage(config.area || 'local');
  if (!storage) {
    return;
  }

  try {
    const payload: PersistedEnvelope<T> = {
      version: config.version || DEFAULT_VERSION,
      value,
    };
    storage.setItem(resolveStorageKey(config), JSON.stringify(payload));
  } catch {
    // no-op: storage may be full or blocked
  }
};

export const removeUiState = (config: UiStateStorageConfig): void => {
  const storage = getStorage(config.area || 'local');
  if (!storage) {
    return;
  }

  try {
    storage.removeItem(resolveStorageKey(config));
  } catch {
    // no-op
  }
};

export const UI_STATE_KEYS = {
  monthPeriodPicker: 'month_period_picker',
} as const;
